import { Loader } from "@common/Loader/Loader";
import { useStyle } from "./style";
import { Grid } from "@mui/material";
import { portInPhoneRegex } from "@utils/regex";
import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import type { FieldError, FieldValues, UseFormMethods } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { LabeledTextField } from "@common/LabeledTextField/LabeledTextField";
import { LabeledButton } from "@common/LabeledButton/LabeledButton";
import { getObjectByPath } from "@utils/getObjectByPath";

interface IParams<TFieldValues extends FieldValues = FieldValues> {
    errorMessage?: string;
    hasTooManyAttempt: boolean;
    phoneNumberSelected: string;
    isGettingCode: boolean;
    isLoading: boolean;
    isSuccess: boolean;
    formMethods: UseFormMethods<TFieldValues>;
    previousPhoneNumber?: string;
    changeValue(newPhoneNumber: string): void;
    getCode(): void;
    namePrefix?: string;
}

export const PhoneNumberView = <TFieldValues extends FieldValues = FieldValues>(props: IParams<TFieldValues>) => {
    const {
        isSuccess,
        isGettingCode,
        formMethods,
        changeValue,
        previousPhoneNumber,
        isLoading,
        hasTooManyAttempt,
        errorMessage,
        getCode,
        phoneNumberSelected,
        namePrefix,
    } = props;

    const { errors, register } = formMethods;

    const { t } = useTranslation(["portin", "validation"]);
    const { classes } = useStyle();

    const phoneNumberFieldName = namePrefix ? `${namePrefix}phoneNumber` : "phoneNumber";
    const phoneNumber = useWatch<string>({
        name: phoneNumberFieldName,
        control: formMethods.control,
        defaultValue: "",
    });
    const getCodeButtonName =
        phoneNumber && phoneNumber === previousPhoneNumber ? t("portin:confirmNumber") : t("portin:getCode");

    const tooltip = useMemo(() => {
        if (phoneNumber.length !== 10 || !isGettingCode || isSuccess) {
            return "";
        }

        return phoneNumber === previousPhoneNumber
            ? t("portin:confirmNumberTooltip")
            : t("portin:validationCodeTooltip");
    }, [isGettingCode, isSuccess, phoneNumber, previousPhoneNumber, t]);

    const tooManyAttemptsMessage = hasTooManyAttempt ? t("portin:tooManyAttempts") : undefined;
    const fieldError = getObjectByPath<FieldError>(errors, phoneNumberFieldName);
    const validationErrorMessage = fieldError?.message ?? tooManyAttemptsMessage ?? errorMessage;

    const validationMessage = isSuccess ? t("portin:mobileNumberValidated") : validationErrorMessage;
    const helperText = validationMessage ?? tooltip ?? "";

    const hasFieldError = !isSuccess && Boolean(validationErrorMessage);

    return (
        <Grid className={classes.phoneRow} container spacing={2}>
            <Grid className={classes.phoneRowInput} item md={6} xs={12}>
                <LabeledTextField
                    disabled={isSuccess}
                    error={hasFieldError}
                    fullWidth
                    helperText={helperText}
                    id="phoneNumber"
                    inputRef={register({
                        required: t("validation:required"),
                        pattern: { value: portInPhoneRegex, message: t("portin:phoneNumberError") },
                    })}
                    isReadOnly={isSuccess}
                    isRequired
                    labelText={t("portin:phoneNumber")}
                    margin="dense"
                    name={phoneNumberFieldName}
                    placeholder="0857123456"
                    value={isSuccess ? phoneNumberSelected || phoneNumber : undefined}
                    onChange={(event) => changeValue(event.target.value)}
                />
            </Grid>
            {isGettingCode && (
                <Grid className={classes.phoneRowButton} item marginBottom={1} md={6} xs={12}>
                    {isLoading && <Loader size={40} />}
                    {!isLoading && (
                        <LabeledButton
                            color="primary"
                            id="getCodeButton"
                            labelText={getCodeButtonName}
                            onClick={getCode}
                        >
                            {getCodeButtonName}
                        </LabeledButton>
                    )}
                </Grid>
            )}
        </Grid>
    );
};
